/**
 * The browser behaviours that give an app away.
 *
 * This runs in a tab, and a tab has habits an application does not: it reloads
 * when you press a key, it offers "Save image as…" over an avatar, and it lets
 * you drag-select the sidebar into a blue smear. None of those are features
 * here — they are the seams of the container showing through.
 *
 * Three rules, and each one has an escape hatch, because an app that traps you
 * is worse than an app that feels like a web page:
 *
 *   1. Reload keys are swallowed — but only the keys. The toolbar button, the
 *      address bar and the menu still reload, so nobody is ever stuck in a
 *      broken build with no way out. What this actually buys is the draft you
 *      were three sentences into when your hand found F5.
 *
 *   2. The native context menu is suppressed where the app has its own, and
 *      left alone over anything editable or selected. Right-clicking a text
 *      field to paste is muscle memory that predates this product, and the
 *      browser's menu is genuinely better at it than we would be.
 *
 *   3. Chrome is not selectable; content is. A channel name that highlights
 *      when you drag past it looks like a document. A message that *cannot* be
 *      selected is a message you cannot quote, which is far worse.
 *
 * Installed once from the root and never removed — there is no state here to
 * leak, and the listeners outlive every screen.
 */

function isEditable(node: EventTarget | null): boolean {
  if (!(node instanceof HTMLElement)) return false;
  const tag = node.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    node.isContentEditable ||
    node.closest('input, textarea, [contenteditable="true"]') !== null
  );
}

/** Is the pointer over text the person has actually selected? */
function hasSelection(): boolean {
  const selection = window.getSelection();
  return selection !== null && !selection.isCollapsed;
}

export function installAppFeel(): void {
  window.addEventListener(
    'keydown',
    (event) => {
      const key = event.key;
      const reloadKey =
        key === 'F5' || ((event.ctrlKey || event.metaKey) && (key === 'r' || key === 'R'));
      if (!reloadKey) return;
      // Shift+Ctrl+R is the "I know what I am doing, bypass the cache" gesture.
      // Somebody reaching for that is debugging, and taking it from them would
      // be the one refusal that costs more than it saves.
      if (event.shiftKey) return;
      event.preventDefault();
    },
    // Capture, so a field that stops propagation cannot let a reload through.
    { capture: true },
  );

  window.addEventListener('contextmenu', (event) => {
    if (isEditable(event.target) || hasSelection()) return;
    // Anything with its own menu calls preventDefault itself and this never
    // sees it; everything else gets nothing rather than the browser's.
    if (event.defaultPrevented) return;
    event.preventDefault();
  });

  /*
   * Dragging a link or an image out of the window is a file-manager gesture,
   * and every draggable thing in here is chrome. Messages stay selectable, so
   * dragging *text* still works the way it does in any editor.
   */
  window.addEventListener('dragstart', (event) => {
    if (isEditable(event.target) || hasSelection()) return;
    event.preventDefault();
  });
}
